import { Container, type Texture } from 'pixi.js';
import type { BoardRunState, BoardStopResult, RunPot } from '@goodtime/shared';
import { AudioBus } from '../../audio/AudioBus';
import { chaseDelays, chasePath } from '../../logic/road/ringLayout';
import { landingSound, ROAD_SOUNDS } from '../../logic/road/sounds';
import { BoardRing, type BoardRingOptions } from '../../widgets/road/BoardRing';
import { prefersReducedMotion, REDUCED_STEP_MS } from '../../widgets/road/glow';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

/** How far the STOP button reaches up over the bottom of the reels. */
const STOP_OVERLAP = 22;

/** How fast the light goes round while waiting for STOP. */
const IDLE_STEP_MS = 85;

/**
 * Plays a Lamp Run over the reels. While waiting for STOP the light runs
 * round the ring; {@link landOn} chases at least one more lap and eases onto
 * the cell the server picked, so a quick answer still looks like a spin.
 * Everything moves on the scene ticker ({@link tick}); with reduced motion
 * the light steps every 300 ms.
 */
export class BoardRunComponent extends Container implements SceneComponent {
  private readonly ring: BoardRing;
  private readonly reduced = prefersReducedMotion();
  private count = 12;
  private lightAt = 0;
  private clock = 0;
  private idle = false;
  private path: number[] = [];
  private delays: number[] = [];
  private landed: (() => void) | null = null;
  /** The chase sounds tick, tock, tick... */
  private tock = false;
  private lastCtx: SceneLayoutContext | null = null;

  private readonly ringTexture: () => Texture | null;
  private readonly hubTexture: () => Texture | null;

  /** `ringTexture` and `hubTexture` are looked up when each run starts,
   *  since the road's art loads after the scene is built. */
  constructor(
    options: Omit<BoardRingOptions, 'ringTexture'> & { ringTexture?: () => Texture | null; hubTexture?: () => Texture | null } = {},
  ) {
    super();
    this.ringTexture = options.ringTexture ?? (() => null);
    this.hubTexture = options.hubTexture ?? (() => null);
    this.ring = new BoardRing({ ...options, ringTexture: this.ringTexture() });
    this.addChild(this.ring);
    this.visible = false;
  }

  get active(): boolean {
    return this.visible;
  }

  /** Show a run (new or resumed) and set the light going. `quiet` skips
   *  the opening sounds (a title card has just played them). */
  start(run: BoardRunState, quiet = false): void {
    this.ring.setRingTexture(this.ringTexture());
    this.ring.setHubTexture(this.hubTexture());
    this.ring.setRun(run.ring, run.lucky);
    this.ring.setPot(run.pot);
    this.count = run.ring.length || 12;
    this.lightAt = 0;
    if (this.lastCtx) this.layout(this.lastCtx);
    this.ring.setLight(0);
    this.ring.visible = true;
    this.visible = true;
    this.idle = true;
    if (quiet) return;
    AudioBus.shared.play(ROAD_SOUNDS.sweep, { volume: 0.7 });
    setTimeout(() => AudioBus.shared.play(ROAD_SOUNDS.runStart, { volume: 0.8 }), 160);
  }

  /** Chase onto `cell`; resolves once the light has settled there. */
  landOn(cell: number): Promise<void> {
    this.idle = false;
    this.path = chasePath(this.count, this.lightAt, cell, 1);
    this.delays = this.reduced ? this.path.map(() => REDUCED_STEP_MS) : chaseDelays(this.path.length);
    this.clock = 0;
    return new Promise((resolve) => {
      this.landed = resolve;
    });
  }

  /** Show a landing: the pot updates and the cell pops (or shudders). */
  showResult(result: BoardStopResult): void {
    this.ring.setPot(result.pot);
    this.ring.flashCell(result.cell, result.kind);
    AudioBus.shared.play(landingSound(result.kind), { volume: 0.8 });
    if (result.kind === 'bomb') setTimeout(() => AudioBus.shared.play(ROAD_SOUNDS.acornUsed, { volume: 0.7 }), 280);
  }

  /** The run pot after a lit lamp's game paid into it. */
  showPot(pot: RunPot): void {
    this.ring.setPot(pot);
  }

  /** Where the run pot shows on the ring, in canvas pixels. */
  get potCentre(): { x: number; y: number } {
    return this.ring.potCentre;
  }

  /** Set the light going again for the next STOP. */
  resume(): void {
    this.idle = true;
  }

  end(): void {
    this.visible = false;
    this.ring.visible = false;
    this.idle = false;
    this.path = [];
    this.delays = [];
    const landed = this.landed;
    this.landed = null;
    landed?.();
  }

  layout(ctx: SceneLayoutContext): void {
    this.lastCtx = ctx;
    const left = ctx.machineLeft ?? 0;
    const right = ctx.machineRight ?? ctx.width;
    const ring = ctx.frameRing ?? 0;
    const reels = { x: left + ring, y: ctx.machineTop + ring, w: right - left - ring * 2, h: ctx.machineBottom - ctx.machineTop - ring * 2 };
    // The cells reach about 0.91 of `size` across, so this keeps the whole
    // ring inside the reels, clear of the STOP button that overlaps the
    // frame's bottom edge.
    const size = Math.min(reels.w, (reels.h - STOP_OVERLAP) / 0.95, 520);
    this.ring.layout((left + right) / 2, (ctx.machineTop + ctx.machineBottom - STOP_OVERLAP) / 2, size, reels);
    this.ring.setLight(this.lightAt);
  }

  tick(deltaMs: number): void {
    if (!this.visible) return;
    this.clock += deltaMs;
    if (this.path.length > 0) {
      // Several steps in one frame when frames are slow, so the chase keeps
      // time; one tick per frame is enough to hear.
      let stepped = false;
      while (this.path.length > 0 && this.clock >= (this.delays[0] ?? 0)) {
        this.clock -= this.delays.shift() ?? 0;
        this.lightAt = this.path.shift() ?? this.lightAt;
        this.ring.setLight(this.lightAt);
        stepped = true;
      }
      if (stepped && this.path.length > 0) {
        this.tock = !this.tock;
        AudioBus.shared.play(this.tock ? ROAD_SOUNDS.chaseTock : ROAD_SOUNDS.chaseTick, { volume: 0.6 });
      }
      if (this.path.length === 0) {
        AudioBus.shared.play(ROAD_SOUNDS.landed, { volume: 0.8 });
        this.clock = 0;
        const landed = this.landed;
        this.landed = null;
        landed?.();
      }
      return;
    }
    const step = this.reduced ? REDUCED_STEP_MS : IDLE_STEP_MS;
    if (this.idle && this.clock >= step) {
      this.clock %= step;
      this.lightAt = (this.lightAt + 1) % this.count;
      this.ring.setLight(this.lightAt);
    }
  }
}
